import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { UniversityDialogProps } from '../types';

export function UniversityDialog({
  isDark,
  isOpen,
  onOpenChange,
  formData,
  onFormDataChange,
  onSubmit,
  title,
  description,
  submitLabel,
}: UniversityDialogProps) {
  const inputClass = cn(
    isDark
      ? 'bg-slate-800 border-slate-700 text-white'
      : 'bg-white border-slate-200'
  );
  const labelClass = cn(isDark ? 'text-slate-300' : 'text-slate-700');

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className={cn(
        'max-w-2xl max-h-[90vh] overflow-y-auto',
        isDark ? 'bg-[#1E293B] border-slate-700' : 'bg-white'
      )}>
        <DialogHeader>
          <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#0F172A]')}>
            {title}
          </DialogTitle>
          <DialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>
            {description}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Names */}
          <div className="space-y-2">
            <Label className={labelClass}>Name (Uzbek) *</Label>
            <Input
              value={formData.name}
              onChange={(e) => onFormDataChange(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Toshkent davlat texnika universiteti"
              className={inputClass}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={labelClass}>Name (Russian)</Label>
              <Input
                value={formData.name_ru || ''}
                onChange={(e) => onFormDataChange(prev => ({ ...prev, name_ru: e.target.value }))}
                placeholder="Ташкентский ГТУ"
                className={inputClass}
              />
            </div>
            <div className="space-y-2">
              <Label className={labelClass}>Name (Cyrillic)</Label>
              <Input
                value={formData.name_kiril || ''}
                onChange={(e) => onFormDataChange(prev => ({ ...prev, name_kiril: e.target.value }))}
                className={inputClass}
              />
            </div>
          </div>

          {/* Slug */}
          <div className="space-y-2">
            <Label className={labelClass}>Slug *</Label>
            <Input
              value={formData.slug}
              onChange={(e) => onFormDataChange(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') }))}
              placeholder="toshkent-davlat-texnika-universiteti"
              className={cn(inputClass, 'font-mono text-sm')}
            />
          </div>

          {/* Location & Region */}
          <div className="space-y-2">
            <Label className={labelClass}>Location</Label>
            <Input
              value={formData.location || ''}
              onChange={(e) => onFormDataChange(prev => ({ ...prev, location: e.target.value }))}
              placeholder="Full address"
              className={inputClass}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={labelClass}>Region</Label>
              <Input
                value={formData.region || ''}
                onChange={(e) => onFormDataChange(prev => ({ ...prev, region: e.target.value }))}
                placeholder="Toshkent"
                className={inputClass}
              />
            </div>
            <div className="space-y-2">
              <Label className={labelClass}>Phone</Label>
              <Input
                value={formData.phone || ''}
                onChange={(e) => onFormDataChange(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="0(371) 241 10 84"
                className={inputClass}
              />
            </div>
          </div>

          {/* Website */}
          <div className="space-y-2">
            <Label className={labelClass}>Website</Label>
            <Input
              value={formData.website || ''}
              onChange={(e) => onFormDataChange(prev => ({ ...prev, website: e.target.value }))}
              placeholder="https://university.uz"
              className={inputClass}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className={cn(
              isDark
                ? 'border-slate-700 text-slate-300 hover:bg-slate-800'
                : 'border-slate-200'
            )}
          >
            Cancel
          </Button>
          <Button onClick={onSubmit} className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90">
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
